import { Stack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { User } from '@/api/User';
import { userService } from '@/service/UserService';
import AbsenceBalanceSection from './AbsenceBalanceSection';
import WorkModelEntrySection from './WorkModelEntrySection';
import TimesheetBalanceSection from './TimesheetBalanceSection';
import ContractTabs from '@/components/ContractTabs';
import { workmodelService } from '@/service/WorkModelService';
import { ContractSummary } from '@/api/ContractSummary';
import UserProfileSection from './UserProfileSection';
import { StatusUtil } from '@/data/StatusUtil';
import UserReportingSection from './UserReportingSection';
import SectionPanel from '@/components/SectionPanel';
import WaitingSpinner from '@/components/WaitingSpinner';
import UserAccessSection from './UserAccessSection';
import { useAuthContext } from '@/auth/AuthContext';
import { OrgUtil } from '@/data/OrgUtil';

type UserSetupProps = {
    userId: number;
    canEditProfile?: boolean;
    canEditSetup?: boolean;
    canEditReporting?: boolean;
};

const UserSetup = ({
    userId,
    canEditProfile = true,
    canEditSetup = true,
    canEditReporting = true,
}: UserSetupProps) => {
    const { user: currentUser, login, logout } = useAuthContext();
    const currentRole = currentUser?.data.role;
    const canEditAccess = Boolean(currentRole && (OrgUtil.isRoleAdmin(currentRole) || OrgUtil.isRoleRoot(currentRole)));
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [contract, setContract] = useState<ContractSummary | null>(null);
    const [org, setOrg] = useState<User[]>([]);
    const shouldShowContractData = user !== null && !OrgUtil.isRoleExtern(user.role);

    useEffect(() => {
        if (!Number.isFinite(userId)) return;

        setIsLoading(true);
        setUser(null);
        setContract(null);

        const usersPromise = userService.listAll()
            .then(r => {
                const all = r.results.filter(u => StatusUtil.isStatusActive(u.status)).filter(u => u.id !== userId);
                setOrg(all);
                const selectedUser = r.results.find(u => u.id === userId);
                if (selectedUser) {
                    setUser(selectedUser);
                    return selectedUser;
                }

                return userService.find(userId).then(found => {
                    setUser(found);
                    return found;
                });
            })
            .catch(() => userService.find(userId).then(found => {
                setUser(found);
                setOrg([]);
                return found;
            }));

        usersPromise.finally(() => setIsLoading(false));
    }, [userId]);

    useEffect(() => {
        setContract(null);
        if (!shouldShowContractData) return;
        let cancelled = false;
        workmodelService.contractSummary(userId).then(summary => {
            if (!cancelled) setContract(summary);
        });
        return () => { cancelled = true; };
    }, [shouldShowContractData, userId]);

    const handleAccessChange = (updatedUser: User) => {
        setUser(updatedUser);
        if (currentUser?.data.id === updatedUser.id) {
            if (StatusUtil.isStatusActive(updatedUser.status)) {
                login({ ...currentUser, data: updatedUser });
            } else {
                logout();
            }
        }
    };

    return (
        <Stack gap='6' minW='0' w='full' maxW='3xl' mx='auto'>
            {shouldShowContractData && (
                <ContractTabs summary={contract} monthMinutes={contract?.workedMinutesThisMonth} month={new Date()} />
            )}
            <SectionPanel title='Profil' actions={isLoading && <WaitingSpinner size='sm' />}>
                <UserProfileSection
                    user={user}
                    setIsLoading={setIsLoading}
                    onUserChange={setUser}
                    canEdit={canEditProfile}
                />
            </SectionPanel>
            <SectionPanel title='Zugriff'>
                <UserAccessSection key={user?.id ?? 'none'} user={user} canEdit={canEditAccess} onUserChange={handleAccessChange} />
            </SectionPanel>
            <SectionPanel title='Berichte' actions={isLoading && <WaitingSpinner size='sm' />}>
                <UserReportingSection
                    user={user}
                    org={org}
                    setIsLoading={setIsLoading}
                    canEdit={canEditReporting}
                />
            </SectionPanel>
            {shouldShowContractData && (<>
            <SectionPanel title='Anstellungsverhältnis'>
                <WorkModelEntrySection user={user} canEdit={canEditSetup && Boolean(user)} />
            </SectionPanel>
            <SectionPanel title='Ferienanpassungen'>
                <AbsenceBalanceSection user={user} canEdit={canEditSetup && Boolean(user)} />
            </SectionPanel>
            <SectionPanel title='Stundensaldo Anpassungen'>
                <TimesheetBalanceSection user={user} canEdit={canEditSetup && Boolean(user)} />
            </SectionPanel>
            </>)}
        </Stack>
    );
};

export default UserSetup;
